import styled from '@emotion/styled'
import { PictureAsPdf } from '@mui/icons-material'
import { Box, Card, Typography } from '@mui/material'
import { Error } from 'components/Error'
import { Loading } from 'components/Loading'
import { CustomPagination } from 'components/purchase/CustomPagination'
import { PurchaseLayout } from 'hoc/PurchaseLayout'
import { formatTimeStampToStandartDate } from 'hooks/date/formattedTime'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryState } from 'react-router-use-location-state'
import { useDownloadFileMutation, useGetFileQuery } from 'services/file'
import { useReportsQuery } from 'services/info-center/reports'
import { downloadFile as downloadFileHandler } from 'utils/download-file'

const breadCrumbs = [
  {
    id: 'home',
    title: 'Ana səhifə',
    path: '/',
  },
  {
    id: 'reports',
    title: 'Hesabat',
  },
]

const StyledBox = styled(Box)({
  background: '#fafbfb',
  border: '1px solid #f1f2f4',
  borderRadius: '4px',
  padding: '14px 16px',
  display: 'flex',
  alignItems: 'baseline',
  cursor: 'pointer',
  textDecoration: 'none',
  '&:not(:last-of-type)': {
    marginBottom: '24px',
  },
})

export const Reports: React.FC = () => {
  const [breadCrumbItems, setBreadCrumbItems] = useState(breadCrumbs)
  const [fileId, setFileId] = useState('')
  const [page, setPage] = useQueryState('page', 0)
  const rowsPerPage = 10
  const { t } = useTranslation()

  const args = {
    page,
    size: rowsPerPage,
  } as const

  const { data, isError, isLoading } = useReportsQuery(args, {
    refetchOnMountOrArgChange: true,
  })

  const { data: file } = useGetFileQuery({ id: fileId ?? '' }, { skip: !fileId })

  const [downloadFile, { isLoading: isDownloadLoading }] = useDownloadFileMutation()

  const onClickDownload = async (fileId: string) => {
    setFileId(fileId)
    await downloadFile({ id: fileId ?? '' })
      .unwrap()
      .then((blob) => {
        downloadFileHandler(blob, file?.originalName ?? '')
      })
  }

  const renderContent = () => {
    return (
      <>
        <Card>
          <Typography variant="h5">{t('reports')}</Typography>
          {data?.items.map((report) => {
            return (
              <StyledBox
                key={report.id}
                onClick={() => onClickDownload(report.reportFileId)}
              >
                <Box display="flex">
                  <PictureAsPdf
                    sx={{
                      color: '#f03d3d',
                      marginRight: '13px',
                      lineHeight: '20px',
                      fontSize: '20px',
                      marginTop: '7px',
                    }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        color: '#8a94a6',
                        marginBottom: '4px',
                      }}
                      variant="caption"
                    >
                      {formatTimeStampToStandartDate(report.publishedAt)}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: '400',
                        fontSize: '14px',
                        lineHeight: '20px',
                        color: '#283a5b',
                        marginBottom: '4px',
                      }}
                    >
                      {report.title}
                    </Typography>
                  </Box>
                </Box>
              </StyledBox>
            )
          })}
        </Card>
        {data?.info.totalRows !== 0 && (
          <CustomPagination
            total={data?.info.totalRows!}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={setPage}
          />
        )}
      </>
    )
  }

  if (isLoading) {
    return <Loading fullPage={false} />
  }
  if (isError) {
    return <Error fullPage={false} />
  }
  if (data === undefined) {
    return <></>
  }

  return (
    <PurchaseLayout breadCrumbItems={breadCrumbItems} content={renderContent()} />
  )
}
